/**
 * Shared constants for keyword review components.
 *
 * Contains category definitions, match type styles, approval statuses,
 * and utility helpers used across the keyword review panel.
 */

import React from 'react';
import { BatteryCharging, EyeOff, Sprout, ShoppingBag, HelpCircle } from "lucide-react";
import type { MatchTypeStyle } from './types';

export const apps = {
  google_ads: {
    app_name: 'google_ads',
    auth_name: 'default',
  },
  tavily: {
    app_name: 'tavily',
    auth_name: 'default',
  }
}

export enum MatchType {
  PHRASE = 'PHRASE',
  EXACT = 'EXACT',
  BROAD = 'BROAD'
}

export const MatchTypeLabel: Record<MatchType, string> = {
  [MatchType.PHRASE]: 'Phrase Match',
  [MatchType.EXACT]: 'Exact Match',
  [MatchType.BROAD]: 'Broad Match'
}

export enum UploadLevel  {
  CAMPAIGN = 'campaign',
  NEGATIVE_KEYWORDS_LIST= 'negative_keywords_list'
}

export const intervalOptions = [
  {
    id: 30,
    value: '30 days'
  },
  {
    id: 60,
    value: '60 days'
  },
  {
    id: 90,
    value: '90 days'
  },
  {
    id: 180,
    value: '180 days'
  }
];

export const APPROVAL_STATUS = {
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  PENDING: null,
  IGNORED: 'IGNORED'
} as const;

export const APPROVAL_STATUS_LABELS = {
  [APPROVAL_STATUS.APPROVED]: 'Approved',
  [APPROVAL_STATUS.REJECTED]: 'Rejected',
  [APPROVAL_STATUS.PENDING]: 'Pending',
  [APPROVAL_STATUS.IGNORED]: 'Ignored'
} as const;

// Category keys
export const CATEGORY_KEYS = {
  BUDGET_DRAINER: "potential budget drainer",
  LOW_INTENT: "potential low intent",
  WINDOW_SHOPPING: "potential window shopping",
  NASCENT_SPENDER: "potential nascent spender"
};

// expected fixed order of categories
export const categoryOrder = [
  CATEGORY_KEYS.BUDGET_DRAINER,
  CATEGORY_KEYS.LOW_INTENT,
  CATEGORY_KEYS.WINDOW_SHOPPING,
  CATEGORY_KEYS.NASCENT_SPENDER
];

// Category details type
export type CategoryDetail = {
  displayName: string;
  description: string;
  icon: React.ReactNode;
};

// Category display information with icons
export const CATEGORY_DETAILS: Record<string, CategoryDetail> = {
  [CATEGORY_KEYS.BUDGET_DRAINER]: {
    displayName: "Budget Drainers",
    description: "Unusually high spend for little to no conversion",
    icon: React.createElement(BatteryCharging, { className: "h-5 w-5 text-primary" })
  },
  [CATEGORY_KEYS.LOW_INTENT]: {
    displayName: "Low Intent",
    description: "High impression volume with low interaction",
    icon: React.createElement(EyeOff, { className: "h-5 w-5 text-red-500" })
  },
  [CATEGORY_KEYS.NASCENT_SPENDER]: {
    displayName: "Nascent Spenders",
    description: "Very low impressions but already wasting budget",
    icon: React.createElement(Sprout, { className: "h-5 w-5 text-emerald-500" })
  },
  [CATEGORY_KEYS.WINDOW_SHOPPING]: {
    displayName: "Window Shoppers",
    description: "High click volume with low conversion rate",
    icon: React.createElement(ShoppingBag, { className: "h-5 w-5 text-blue-500" })
  }
};

// Default category display info (fallback)
export const DEFAULT_CATEGORY_DETAIL: CategoryDetail = {
  displayName: "Other Category",
  description: "Keywords grouped by similar characteristics",
  icon: React.createElement(HelpCircle, { className: "h-5 w-5 text-gray-500" })
};

/**
 * Helper function to get category details with fallback to default
 * @param categoryKey The category key from the backend
 * @returns The category details including display name, description and icon
 */
export const getCategoryDetail = (categoryKey: string): CategoryDetail => {
  return CATEGORY_DETAILS[categoryKey] || DEFAULT_CATEGORY_DETAIL;
};

// Match type styling
export const defaultMatchTypeStyles: Record<string, MatchTypeStyle> = {
  "BROAD": {
    background: "bg-blue-100",
    text: "text-blue-800",
    border: "border-blue-200"
  },
  "PHRASE": {
    background: "bg-green-100",
    text: "text-green-800",
    border: "border-green-200"
  },
  "NEAR_PHRASE": {
    background: "bg-emerald-100",
    text: "text-emerald-800",
    border: "border-emerald-200"
  },
  "EXACT": {
    background: "bg-purple-100",
    text: "text-purple-800",
    border: "border-purple-200"
  },
  "NEAR_EXACT": {
    background: "bg-violet-100",
    text: "text-violet-800",
    border: "border-violet-200"
  },
  "PERFORMANCE_MAX": {
    background: "bg-blue-100",
    text: "text-blue-800",
    border: "border-blue-200"
  }
};
